// Builds the POST /what-if-scenario-to-be request body (see
// what_if_scenario_to_be_documentation.md) from the live scenario draft and
// its last queuing run. Kept separate from whatIfScenario.js /
// whatIfQueueing.js since this is wire-shape mapping, not scenario math.

import { deriveQueueJobs, readStaffCount } from "./whatIfQueueing";

const round2 = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

// The app's own time-unit values are "minutes"/"hours" (see TIME_UNITS in
// whatIfFormat.js), used throughout for display formatting. The backend's
// `timeUnit` enum is singular and uppercase (`MINUTE` | `HOUR`) — mapped only
// at this wire boundary, not by renaming the value used everywhere else.
const TIME_UNIT_WIRE = {
  minutes: "MINUTE",
  hours: "HOUR",
};

const toWireTimeUnit = (timeUnit) => TIME_UNIT_WIRE[timeUnit] || "HOUR";

// One row per RACI slot that actually has a job assigned — a role slot the
// analyst opened but never filled has nothing to persist yet, and the schema
// has no "unassigned" representation for a RACI item.
function buildRaciMatrixToBe(scenario) {
  const items = [];
  scenario.tasks.forEach((entry) => {
    const taskId = Number(entry.task.taskId);
    if (!Number.isFinite(taskId)) return;
    const participantCount = entry.slots.filter((s) => s.slot.jobId != null).length;

    entry.slots.forEach(({ slot, minutes, costUsd }) => {
      if (slot.jobId == null || slot.jobId === "") return;
      items.push({
        job_id: Number(slot.jobId),
        task_id: taskId,
        role: slot.role,
        time_allocation_percentage: round2(slot.allocationPercent) ?? 0,
        minutes_total: round2(minutes) ?? 0,
        total_cost_usd: round2(costUsd) ?? 0,
        eliminated: entry.eliminated,
        participant_count: participantCount,
      });
    });
  });
  return items;
}

// One row per job with a non-eliminated role in the scenario, using the
// scenario's own service-time figure (not re-derived from the queuing
// response) so it can never disagree with what the Queuing tab showed.
// `WeeklyLoad`/`queueWaitingPerCase` come from the last run and are null if
// that job wasn't part of it (e.g. added to the scenario after the last run).
function buildQueuingAnalysis(scenario, queueing, processId, companyId) {
  if (!queueing?.result) return [];
  const byJobId = queueing.result.byJobId || {};

  return deriveQueueJobs(scenario).map((job) => {
    const computed = byJobId[String(job.jobId)] || null;
    return {
      processId: Number(processId),
      ...(companyId != null ? { companyId: Number(companyId) } : {}),
      job_id: Number(job.jobId),
      staff: Number(
        readStaffCount(queueing.staffByJob, job.jobId, job.recordedStaffCount),
      ) || 1,
      servicePerCase: round2(job.serviceMinutesPerCase) ?? 0,
      WeeklyLoad: computed ? round2(computed.weeklyWorkloadHours) : null,
      queueWaitingPerCase: computed ? round2(computed.queueWaitMinutesPerCase) : null,
    };
  });
}

/**
 * `cycleTimeToBe` is the scenario's true to-be process duration, computed by
 * the caller (ScenarioEditor.js) as the backend's real as-is Cycle Time plus
 * this session's task-duration delta — NOT a resum of the flat task rows,
 * which can't include subprocess time or (for some processes) per-task
 * waiting/rework figures at all. Passed in ready-made rather than derived
 * here so this stays wire-shape mapping, not scenario math.
 */
export function buildScenarioToBePayload({
  scenario,
  queueing,
  processId,
  companyId,
  name,
  description,
  laborCostPerCase,
  laborCostPerCaseToBe,
  cycleTimeToBe,
  timeUnit,
  currency,
}) {
  return {
    processId: Number(processId),
    ...(companyId != null ? { companyId: Number(companyId) } : {}),
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    labourCostToBe: round2(laborCostPerCaseToBe),
    currentProcessCost: round2(laborCostPerCase),
    cycleTimeToBe: round2(cycleTimeToBe),
    staffCostToBe: round2(scenario.costUsd),
    timeUnit: toWireTimeUnit(timeUnit),
    currency,
    raciMatrixToBe: buildRaciMatrixToBe(scenario),
    queuingAnalysis: buildQueuingAnalysis(scenario, queueing, processId, companyId),
  };
}
